import React, { useState } from "react";
import { Anchor, Skeleton } from "@mantine/core";
import { useDataProvider } from "@hooks/data";
import { previewService, PreviewData } from "@services/previewService";

interface BasePreviewLinkProps {
  children: React.ReactNode;
  className?: string;
  onGetPreviewData: () => Promise<PreviewData>;
}

const BasePreviewLink: React.FC<BasePreviewLinkProps> = ({ children, className, onGetPreviewData }) => {
  const [loading, setLoading] = useState(false);
  const getDataProvider = useDataProvider();
  const dataProvider = getDataProvider();

  const handleClick = async (e: React.MouseEvent) => {
    e.preventDefault();

    if (loading) return;

    setLoading(true);

    try {
      const previewData: PreviewData = await onGetPreviewData();

      if (previewData.isValid && previewData.authUrl && previewData.token && previewData.redirectPath) {
        const service = previewService(dataProvider);
        service.openPreviewWindow(previewData.authUrl, previewData.token, previewData.redirectPath);
      } else {
        console.error("Invalid preview data:", previewData.error);
        alert(`Failed to open preview: ${previewData.error || "Unknown error"}`);
      }
    } catch (error) {
      console.error("Error opening preview:", error);
      alert("Failed to open preview. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Anchor
      href="#"
      onClick={handleClick}
      className={className}
      style={{
        position: "relative",
        pointerEvents: loading ? "none" : "auto",
      }}
    >
      {loading && (
        <Skeleton
          height="100%"
          width="100%"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            borderRadius: 4,
            opacity: 0.5,
          }}
          animate
        />
      )}
      <span style={{ opacity: loading ? 0 : 1 }}>{children}</span>
    </Anchor>
  );
};

interface PdpPreviewLinkProps {
  dealId: number;
  children: React.ReactNode;
  className?: string;
}

export const PdpPreviewLink: React.FC<PdpPreviewLinkProps> = ({ dealId, children, className }) => {
  const getDataProvider = useDataProvider();
  const dataProvider = getDataProvider();

  const handleGetPreviewData = async (): Promise<PreviewData> => {
    const service = previewService(dataProvider);
    return await service.getDealPreviewData(dealId);
  };

  return (
    <BasePreviewLink className={className} onGetPreviewData={handleGetPreviewData}>
      {children}
    </BasePreviewLink>
  );
};

interface SlpPreviewLinkProps {
  sellerId: number;
  children: React.ReactNode;
  className?: string;
}

export const SlpPreviewLink: React.FC<SlpPreviewLinkProps> = ({ sellerId, children, className }) => {
  const getDataProvider = useDataProvider();
  const dataProvider = getDataProvider();

  const handleGetPreviewData = async (): Promise<PreviewData> => {
    const service = previewService(dataProvider);
    return await service.getSellerPreviewData(sellerId);
  };

  return (
    <BasePreviewLink className={className} onGetPreviewData={handleGetPreviewData}>
      {children}
    </BasePreviewLink>
  );
};
